// Before any pdf.js import: iOS WebKit lacks Map.prototype.getOrInsertComputed.
import '$lib/lib/mapUpsertPolyfill';
import * as Sentry from '@sentry/browser';
import type { BrowserOptions } from '@sentry/browser';
import { isTauri } from '$lib/platform/detector';
import { onAuthChange } from '$lib/platform/auth-provider';
import type { HandleClientError } from '@sveltejs/kit';
import {
  RELOAD_SUPPRESSED_TAG,
  STALE_CHUNK_ERROR_PATTERNS,
  filterStaleChunkBeforeSend,
  isStaleChunkError,
  scheduleStaleChunkReload,
} from '$lib/sentry/staleChunkError';

const staleChunkSentryOptions: Pick<BrowserOptions, 'ignoreErrors' | 'beforeSend'> = {
  ignoreErrors: [...STALE_CHUNK_ERROR_PATTERNS],
  // beforeSend is the source of truth for captureException / event payloads.
  beforeSend(event, hint) {
    return filterStaleChunkBeforeSend(event, hint);
  },
};

async function initSentry() {
  let environment = 'development';
  if (import.meta.env.PROD) {
    const host = typeof window !== 'undefined' ? window.location.host : '';
    if (host) {
      environment = host.split('.')[0];
      if (environment === 'app-dev') {
        environment = 'staging';
      } else if (environment.startsWith('digital-pvp-')) {
        environment = 'feature-env';
      }
    } else {
      environment = 'production';
    }
  }

  if (isTauri()) {
    const { defaultOptions } = await import('$lib/tauri-sentry');
    Sentry.init({
      ...defaultOptions,
      dsn: 'https://2c27a4a5f71d4eb595cd26ad09730ae5@o1272615.ingest.sentry.io/6466448',
      environment,
      tracesSampleRate: 0.1,
      ...staleChunkSentryOptions,
    });
  } else {
    Sentry.init({
      dsn: 'https://2c27a4a5f71d4eb595cd26ad09730ae5@o1272615.ingest.sentry.io/6466448',
      integrations: [],
      environment,
      tracesSampleRate: 0.1,
      ...staleChunkSentryOptions,
    });
  }
}

initSentry().then(() => {
  // Subscribe to auth state changes and update Sentry user context/tags
  onAuthChange((user) => {
    if (user) {
      const originalUid = (user as any).originalUid;
      Sentry.setUser({
        id: user.uid,
        email: user.email || undefined,
      });
      Sentry.setTag('userId', user.uid);
      if (originalUid && originalUid !== user.uid) {
        Sentry.setTag('adminUserId', originalUid);
        Sentry.setTag('isImpersonating', 'true');
      } else {
        Sentry.setTag('adminUserId', null as any);
        Sentry.setTag('isImpersonating', 'false');
      }
    } else {
      Sentry.setUser(null);
      Sentry.setTag('userId', null as any);
      Sentry.setTag('adminUserId', null as any);
      Sentry.setTag('isImpersonating', 'false');
    }
  });
}).catch((err) => {
  console.error('Failed to initialize Sentry:', err);
});

export const handleError: HandleClientError = ({ error, event, status, message }) => {
  // SvelteKit catches failed dynamic imports on client navigations, so app.html's
  // unhandledrejection listener may never run (JAVASCRIPT-21D). Reload instead of
  // capturing; beforeSend/ignoreErrors are the safety net if this still reaches Sentry.
  if (isStaleChunkError(error)) {
    if (scheduleStaleChunkReload()) {
      return {
        message: message || 'Loading updated application…',
      };
    }
    // Reload cooldown hit: the error persisted after a reload, so report it (tagged so
    // beforeSend lets it through) instead of silently swallowing a real outage.
    Sentry.captureException(error, {
      tags: { [RELOAD_SUPPRESSED_TAG]: 'true' },
      extra: { event, status, message },
    });
    return { message: message || 'Failed to load the application. Please refresh.' };
  }

  Sentry.captureException(error, {
    extra: { event, status, message },
  });

  // Log to console so developers can see the error in DevTools
  console.error(error);

  return {
    message: message || 'An unexpected error occurred.',
  };
};
